import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/cn'
import { useClickOutside } from '@/lib/useClickOutside'

export interface ActionItem {
  label: string
  icon?: ReactNode
  onClick: () => void
  /** Red, and set apart by a divider (e.g. Delete). */
  danger?: boolean
}

const MENU_WIDTH = 208

/**
 * A "⋯" button opening a menu of actions. The menu is portalled to the page and positioned under the
 * button, so a scrolling table (overflow-x) can't clip it. Closes on choice, outside press, Escape or scroll.
 */
export default function ActionMenu({ items, label = 'Actions' }: { items: ActionItem[]; label?: string }) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const open = pos != null
  const close = () => setPos(null)

  useClickOutside(menuRef, (() => close()), open)
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close()
    window.addEventListener('keydown', onKey)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [open])

  function toggle() {
    if (open) return close()
    const r = buttonRef.current?.getBoundingClientRect()
    if (!r) return
    const height = items.length * 36 + 16
    const below = r.bottom + 4 + height < window.innerHeight
    setPos({
      top: below ? r.bottom + 4 : Math.max(8, r.top - 4 - height),
      left: Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8)),
    })
  }

  const regular = items.filter((i) => !i.danger)
  const danger = items.filter((i) => i.danger)

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggle}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        className="w-8 h-8 inline-flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-brand-dark"
      >
        <MoreHorizontal size={17} />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
            className="fixed z-[55] bg-white border border-gray-200 rounded-xl shadow-lg py-1.5"
          >
            {[regular, danger].map((group, gi) =>
              group.length === 0 ? null : (
                <div key={gi} className={cn(gi === 1 && regular.length > 0 && 'border-t border-gray-100 mt-1.5 pt-1.5')}>
                  {group.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        close()
                        item.onClick()
                      }}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left',
                        item.danger ? 'text-danger hover:bg-red-50' : 'text-gray-700 hover:bg-gray-50',
                      )}
                    >
                      <span className={cn('w-4 shrink-0', !item.danger && 'text-gray-400')}>{item.icon}</span>
                      {item.label}
                    </button>
                  ))}
                </div>
              ),
            )}
          </div>,
          document.body,
        )}
    </>
  )
}
